/**
 * Create a remote data fetcher for the <RemoteAutoComplete> component
 *
 * @param getResource A function that takes the current search query and returns
 *                    the resource to fetch (either a URL or a Request object)
 * @param fallbackData The data to return when no remote data is fetched yet
 */
export function createRemoteAutoCompleteDataFetcher<T>(
  getResource: (query: string) => string | Request | undefined,
  fallbackData: T,
): (query: string, { abortSignal }: { abortSignal: AbortSignal }) => Promise<T>

/**
 * Create a remote data fetcher for the <RemoteAutoComplete> component
 *
 * @param getResource A function that takes the current search query and returns
 *                    the resource to fetch (either a URL or a Request object)
 * @param fallbackData The data to return when no remote data is fetched yet
 * @param processResult A function that takes the fetched data and the current search query
 *                      and returns the data to be passed to the component
 */
export function createRemoteAutoCompleteDataFetcher<T, U = T>(
  getResource: (query: string) => string | Request | undefined,
  fallbackData: T,
  processResult: (result: U, query: string) => T,
): (query: string, { abortSignal }: { abortSignal: AbortSignal }) => Promise<T>

export function createRemoteAutoCompleteDataFetcher<T, U = T>(
  getResource: (query: string) => string | Request | undefined,
  fallbackData: T,
  processResult: (result: U, query: string) => T = x => x as unknown as T,
): (query: string, { abortSignal }: { abortSignal: AbortSignal }) => Promise<T> {
  return async (query: string, { abortSignal }: { abortSignal: AbortSignal }) => {
    const resource = getResource(query)
    if (resource == null) return fallbackData

    return fetch(resource, {
      signal: abortSignal,
    })
      .then(result => result.json())
      .then(result => processResult(result, query))
  }
}
